export type SelectionListener = (text: string, rect: DOMRect, range: Range) => void;

// Minimum gap between distinct speak triggers. Edge's ExplainerAgent +
// some Chrome translation features fire selectionchange events very
// rapidly when the user brushes text or drag-selects across paragraphs,
// each of which would otherwise burn a /v1/speak call from the key's
// per-window rate budget. The trailing-edge throttle below caps the
// fire rate at one per 1.2s while still playing the user's MOST RECENT
// selection (not the first one in a burst).
const MIN_INTERVAL_MS = 1200;

/**
 * Watches the document for text selections. When the widget is "active"
 * (pill on), any text the user selects is auto-fired to the listener
 * — there's no explicit Play button. New selections cancel the previous
 * playback in the listener.
 *
 * Two-stage gate:
 *   1. 100ms debounce  — prevents accidental brushes / triple-clicks
 *      from triggering before the user has settled on a selection.
 *   2. 1.2s min-interval throttle — caps the fire rate so rapid distinct
 *      selections (Edge ExplainerAgent, multi-paragraph drag-select,
 *      etc.) don't drain the API key's rate-limit budget. Trailing edge
 *      so the user's latest selection still plays after the cooldown.
 */
export class SelectionWatcher {
  private active = false;
  private lastText = '';
  private lastFiredAt = 0;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private throttle: ReturnType<typeof setTimeout> | null = null;

  constructor(private listener: SelectionListener) {
    this.onSelect = this.onSelect.bind(this);
  }

  enable(): void {
    if (this.active) return;
    this.active = true;
    document.addEventListener('mouseup', this.onSelect);
    document.addEventListener('touchend', this.onSelect);
    document.addEventListener('keyup', this.onSelect);
  }

  disable(): void {
    if (!this.active) return;
    this.active = false;
    document.removeEventListener('mouseup', this.onSelect);
    document.removeEventListener('touchend', this.onSelect);
    document.removeEventListener('keyup', this.onSelect);
    if (this.debounce) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }
    if (this.throttle) {
      clearTimeout(this.throttle);
      this.throttle = null;
    }
    this.lastText = '';
    // Reset the throttle clock too - otherwise a user who toggles the
    // pill off and back on within MIN_INTERVAL_MS still has cooldown
    // state from the previous session leaking through, so their first
    // new selection fires late (or after a stale setTimeout). The
    // watcher should behave as if it's freshly constructed on each
    // enable. Codex on Llais#24.
    this.lastFiredAt = 0;
  }

  private onSelect(): void {
    if (this.debounce) clearTimeout(this.debounce);
    // A new selection in flight: also cancel any pending throttle so
    // the throttle doesn't fire stale text after the user moves on.
    if (this.throttle) {
      clearTimeout(this.throttle);
      this.throttle = null;
    }
    this.debounce = setTimeout(() => {
      this.debounce = null;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (!text || text.length < 2) return;
      if (text === this.lastText) return;
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      // Hand a CLONED range to the listener so the highlighter has a
      // stable reference even if the user's live selection mutates
      // (e.g. browser focus changes between mouseup and audio start).
      const clonedRange = range.cloneRange();

      const fire = (): void => {
        this.throttle = null;
        this.lastText = text;
        this.lastFiredAt = Date.now();
        this.listener(text, rect, clonedRange);
      };

      const elapsed = Date.now() - this.lastFiredAt;
      if (elapsed >= MIN_INTERVAL_MS) {
        fire();
      } else {
        // Trailing-edge: wait out the remaining cooldown then fire the
        // captured text. If the user makes another selection during the
        // wait, the next onSelect() will clear this throttle and start
        // fresh - so what fires is always the latest settled selection.
        this.throttle = setTimeout(fire, MIN_INTERVAL_MS - elapsed);
      }
    }, 100);
  }
}
